import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfToday,
} from 'date-fns';

interface MonthGridProps {
  month: Date;
  selectedDate: Date | null;
  availableDates: Set<string>;
  isLoading: boolean;
  minMonth?: Date;
  onSelectDate: (date: Date) => void;
  onChangeMonth: (newMonth: Date) => void;
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const toKey = (d: Date) => format(d, 'yyyy-MM-dd');

export const MonthGrid: React.FC<MonthGridProps> = ({
  month,
  selectedDate,
  availableDates,
  isLoading,
  minMonth,
  onSelectDate,
  onChangeMonth,
}) => {
  const today = startOfToday();
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const leadingBlanks = getDay(monthStart);

  const lowerBound = minMonth ?? startOfMonth(today);
  const canGoBack = isAfter(monthStart, lowerBound);

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-semibold text-medical-900">
          {format(month, 'MMMM')} <span className="font-normal text-medical-400">{format(month, 'yyyy')}</span>
        </h3>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onChangeMonth(addMonths(month, -1))}
            disabled={!canGoBack}
            aria-label="Previous month"
            className="p-1.5 rounded-md text-medical-500 hover:text-medical-900 hover:bg-medical-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onChangeMonth(addMonths(month, 1))}
            aria-label="Next month"
            className="p-1.5 rounded-md text-medical-500 hover:text-medical-900 hover:bg-medical-100 transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2 border-b border-medical-100 pb-2">
        {DAY_LABELS.map(label => (
          <div
            key={label}
            className="text-center text-[10px] font-semibold uppercase tracking-wider text-medical-400"
          >
            {label}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1" aria-busy={isLoading}>
        {Array.from({ length: leadingBlanks }).map((_, i) => (
          <div key={`blank-${i}`} aria-hidden />
        ))}
        {days.map(day => {
          const inThisMonth = isSameMonth(day, month);
          const isPast = isBefore(day, today);
          const key = toKey(day);
          const isAvailable = availableDates.has(key) && !isPast && inThisMonth;
          const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;
          const isTodayCell = isSameDay(day, today);

          const base =
            'relative aspect-square flex items-center justify-center rounded-full text-sm transition-colors select-none';
          let cls: string;
          if (isSelected) {
            cls = `${base} bg-primary-600 text-white font-semibold`;
          } else if (isAvailable) {
            cls = `${base} text-medical-900 font-medium hover:bg-primary-50 cursor-pointer`;
          } else {
            cls = `${base} text-medical-300 cursor-default`;
          }

          return (
            <button
              key={key}
              type="button"
              disabled={!isAvailable}
              onClick={() => isAvailable && onSelectDate(day)}
              className={cls}
              aria-label={format(day, 'EEEE d MMMM yyyy')}
              aria-pressed={isSelected}
            >
              <span className={isTodayCell && !isSelected ? 'underline underline-offset-4 decoration-primary-500' : ''}>
                {format(day, 'd')}
              </span>
              {isAvailable && !isSelected && (
                <span className="absolute bottom-1 h-1 w-1 rounded-full bg-primary-500" aria-hidden />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
